import React from 'react';

import { TFunction, useTranslation } from 'react-i18next';

import {
  BasePrice,
  DaysOfWeek,
  DynamicPrice,
  PriceType,
  TariffComponent as TariffComponentType,
  TimePrice,
  TimeUnit,
} from 'subdomains/cpo/types/charging-policy';
import { currencyToSymbol } from 'utils/formatting';
import { convertSecondsToHHMM } from 'utils/date';
import { formatDateForDisplay } from 'subdomains/cpo/utils/date';

type Props = {
  values: TariffComponentType;
  currency: string;
  minimal?: boolean;
};

export const PriceAndRestrictionsLabel = ({
  values,
  currency,
  minimal,
}: Props) => {
  const { t } = useTranslation();
  const parts: React.ReactNode[] = [];
  const renderParkingLabel =
    minimal && values.price?.type === PriceType.PARKING_TIME;

  if (!minimal) {
    parts.push(<span>{formatPriceType(values.price, t)}</span>);
    parts.push(': ');
  }
  const restrictionsString = formatRestrictions(values.restrictions, t);
  if (restrictionsString) {
    parts.push(<span>{restrictionsString}</span>);
    parts.push(' - ');
  }
  parts.push(
    <strong>
      {formatPriceValue(values.price, currency, t)}{' '}
      {/* 
        Time tariff and parking tariff can have exacly the same text (eg: $0.1/min and $0.1/min)
        This is why we need to render the suffix '...(parking)' so the user can differentiate between the two
      */}
      {renderParkingLabel &&
        `(${t('tariff.componentPreview.parkingTime', 'Parking')})`}
    </strong>
  );

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>{part}</React.Fragment>
      ))}
    </>
  );
};

function formatPriceType(
  price: TariffComponentType['price'],
  t: TFunction<'translation', undefined>
): string {
  if (!price) return '';

  switch (price?.type) {
    case PriceType.DYNAMIC:
      return t('tariff.componentPreview.priceTypeDynamic', 'Dynamic price');
    case PriceType.ENERGY:
      return t('tariff.componentPreview.priceTypeEnergy', 'Energy price');
    case PriceType.TIME:
      return t('tariff.componentPreview.priceTypeTime', 'Time price');
    case PriceType.PARKING_TIME:
      return t(
        'tariff.componentPreview.priceTypeParkingTime',
        'Parking time price'
      );
    case PriceType.FLAT:
      return t('tariff.componentPreview.priceTypeFlat', 'Flat price');
    default:
      return t(
        'tariff.componentPreview.priceTypeUnknown',
        'Unknown price type'
      );
  }
}

function formatPriceValue(
  price: TariffComponentType['price'],
  currency: string,
  t: TFunction<'translation', undefined>
): string {
  if (!price) return '';

  const currencySymbol = currencyToSymbol(currency);

  switch (price?.type) {
    case PriceType.DYNAMIC:
      return t(
        'tariff.componentPreview.previewDynamicPriceValue',
        'Surcharge: {{currencySymbol}}{{surcharge}}/kWh, Backup: {{currencySymbol}}{{default}}/kWh',
        {
          currencySymbol,
          surcharge: (price as DynamicPrice).surcharge,
          default: (price as DynamicPrice).backup,
        }
      );
    case PriceType.ENERGY:
    case PriceType.FLAT:
      return t(
        'tariff.componentPreview.previewBasePriceValue',
        '{{currencySymbol}}{{price}}/{{unit}}',
        {
          currencySymbol,
          price: (price as BasePrice).value,
          unit: price.type === PriceType.ENERGY ? 'kWh' : 'session',
        }
      );
    case PriceType.TIME:
    case PriceType.PARKING_TIME:
      return t(
        'tariff.componentPreview.previewTimePriceValue',
        '{{currencySymbol}}{{price}}/{{format}}',
        {
          currencySymbol,
          price: (price as TimePrice).value,
          format: (price as TimePrice).unit || 'HOUR',
        }
      );
    default:
      return '';
  }
}

function formatRestrictions(
  restrictions: TariffComponentType['restrictions'],
  t: TFunction<'translation', undefined>
): string {
  if (!restrictions || !restrictions.enabled) return '';

  const restrictionParts: string[] = [];

  const timeOfDayString = formatTimeOfDayRestriction(restrictions.timeOfDay, t);
  if (timeOfDayString) restrictionParts.push(timeOfDayString);

  const durationString = formatDurationRestriction(restrictions.duration, t);
  if (durationString) restrictionParts.push(durationString);

  const daysOfWeekString = formatDaysOfWeekRestriction(
    restrictions.daysOfWeek,
    t
  );
  if (daysOfWeekString) restrictionParts.push(daysOfWeekString);

  const dateString = formatDateRestriction(restrictions.date);
  if (dateString) restrictionParts.push(dateString);

  const energyString = formatEnergyRestriction(restrictions.energy, t);
  if (energyString) restrictionParts.push(energyString);

  return restrictionParts.join(', ');
}

function formatTimeOfDayRestriction(
  timeOfDay: NonNullable<TariffComponentType['restrictions']>['timeOfDay'],
  t: TFunction<'translation', undefined>
): string {
  if (!timeOfDay?.enabled || (!timeOfDay.start && !timeOfDay.end)) return '';

  if (timeOfDay.start && timeOfDay.end) {
    return `${convertSecondsToHHMM(timeOfDay.start)}-${convertSecondsToHHMM(timeOfDay.end)}`;
  } else if (timeOfDay.start) {
    return t('tariff.component.previewTimeOfDayFrom', 'From {{start}}', {
      start: convertSecondsToHHMM(timeOfDay.start),
    });
  } else if (timeOfDay.end) {
    return t('tariff.component.previewTimeOfDayUntil', 'Until {{end}}', {
      end: convertSecondsToHHMM(timeOfDay.end),
    });
  }
  return '';
}

function formatDurationRestriction(
  duration: NonNullable<TariffComponentType['restrictions']>['duration'],
  t: TFunction<'translation', undefined>
): string {
  if (!duration?.enabled || (!duration.min && !duration.max)) return '';

  const unit =
    duration.unit === TimeUnit.HOUR
      ? t('tariff.component.previewDurationRangeHourUnit', 'h')
      : t('tariff.component.previewDurationRangeHourMinute', 'min');
  if (duration.min && duration.max) {
    return t(
      'tariff.component.previewDurationRange',
      '{{min}}{{unit}}-{{max}}{{unit}}',
      {
        min: duration.min,
        max: duration.max,
        unit,
      }
    );
  } else if (duration.min) {
    return t('tariff.component.previewDurationMin', '>{{min}}{{unit}}', {
      min: duration.min,
      unit,
    });
  } else if (duration.max) {
    return t('tariff.component.previewDurationMax', '<{{max}}{{unit}}', {
      max: duration.max,
      unit,
    });
  }
  return '';
}

function formatDaysOfWeekRestriction(
  daysOfWeek: NonNullable<TariffComponentType['restrictions']>['daysOfWeek'],
  t: TFunction<'translation', undefined>
): string {
  if (!daysOfWeek?.enabled || daysOfWeek.values.length === 0) return '';

  const dayAbbreviations: { [key: string]: string } = {
    [DaysOfWeek.MONDAY]: t('tariff.component.previewDaysOfWeekMonday', 'Mon'),
    [DaysOfWeek.TUESDAY]: t('tariff.component.previewDaysOfWeekTuesday', 'Tue'),
    [DaysOfWeek.WEDNESDAY]: t(
      'tariff.component.previewDaysOfWeekWednesday',
      'Wed'
    ),
    [DaysOfWeek.THURSDAY]: t(
      'tariff.component.previewDaysOfWeekThursday',
      'Thu'
    ),
    [DaysOfWeek.FRIDAY]: t('tariff.component.previewDaysOfWeekFriday', 'Fri'),
    [DaysOfWeek.SATURDAY]: t(
      'tariff.component.previewDaysOfWeekSaturday',
      'Sat'
    ),
    [DaysOfWeek.SUNDAY]: t('tariff.component.previewDaysOfWeekSunday', 'Sun'),
  };
  return daysOfWeek.values
    .map((day: string) => dayAbbreviations[day])
    .join(',');
}

function formatDateRestriction(
  date: NonNullable<TariffComponentType['restrictions']>['date']
): string {
  if (!date?.enabled || (!date.start && !date.end)) return '';

  if (date.start && date.end) {
    const start = new Date(date.start);
    const end = new Date(date.end);
    return `${formatDateForDisplay(start)}-${formatDateForDisplay(end)}`;
  } else if (date.start) {
    const start = new Date(date.start);
    return `From ${formatDateForDisplay(start)}`;
  } else if (date.end) {
    const end = new Date(date.end);
    return `Until ${formatDateForDisplay(end)}`;
  }
  return '';
}

function formatEnergyRestriction(
  energy: NonNullable<TariffComponentType['restrictions']>['energy'],
  t: TFunction<'translation', undefined>
): string {
  if (!energy?.enabled || (!energy.min && !energy.max)) return '';

  if (energy.min && energy.max) {
    return t('tariff.component.previewEnergyRange', '{{min}}-{{max}}kWh', {
      min: energy.min,
      max: energy.max,
    });
  } else if (energy.min) {
    return t('tariff.component.previewEnergyMin', '>{{min}}kWh', {
      min: energy.min,
    });
  } else if (energy.max) {
    return t('tariff.component.previewEnergyMax', '<{{max}}kWh', {
      max: energy.max,
    });
  }
  return '';
}
